let csrfRefreshPromise = null;

export function updateCsrfToken(token) {
  if (!token) return null;

  let metaTag = document.querySelector('meta[name="csrf-token"]');

  if (!metaTag) {
    metaTag = document.createElement("meta");
    metaTag.setAttribute("name", "csrf-token");
    document.head.appendChild(metaTag);
  }

  metaTag.setAttribute("content", token);
  return token;
}

export async function refreshCsrfToken() {
  if (!csrfRefreshPromise) {
    csrfRefreshPromise = fetch("/csrf_token", {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`CSRF refresh failed with status ${response.status}`);
        }

        const data = await response.json();
        return updateCsrfToken(data.csrf_token);
      })
      .finally(() => {
        csrfRefreshPromise = null;
      });
  }

  return csrfRefreshPromise;
}

const CSRF_REFRESH_DEBOUNCE_MS = 30_000;
let lastCsrfRefresh = 0;

async function forceCsrfRefresh() {
  lastCsrfRefresh = Date.now();
  try {
    await refreshCsrfToken();
  } catch {
    // Silent fail — next Turbo navigation will refresh the token
  }
}

// Keep <meta name="csrf-token"> in sync with the server session token so a
// state-changing request (e.g. a data-turbo-method link) never submits a stale
// token and 422s. Wire this up once at boot.
export function installCsrfTokenRefresh() {
  // Every Turbo/Ajax response carries a fresh token in the X-CSRF-Token header.
  document.addEventListener("turbo:before-fetch-response", (event) => {
    const token =
      event.detail.fetchResponse?.response?.headers.get("X-CSRF-Token");
    updateCsrfToken(token);
  });

  // Proactive refresh when the app returns to the foreground.
  // Critical for PWA (home screen) and Hotwire Native where the OS may kill
  // the process and restore a cached snapshot with a stale CSRF token.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    if (Date.now() - lastCsrfRefresh < CSRF_REFRESH_DEBOUNCE_MS) return;

    forceCsrfRefresh();
  });

  // Restoration visits (browser/native back-forward) and BFCache restores
  // rebuild the DOM — including the csrf-token meta tag — from a cached snapshot
  // WITHOUT a network fetch, so turbo:before-fetch-response never runs to
  // refresh the token. Re-sync it after the snapshot renders.
  let pendingRestore = false;

  document.addEventListener("turbo:visit", (event) => {
    if (event.detail?.action === "restore") pendingRestore = true;
  });

  document.addEventListener("turbo:load", () => {
    if (!pendingRestore) return;
    pendingRestore = false;

    forceCsrfRefresh();
  });

  window.addEventListener("pageshow", (event) => {
    if (event.persisted) forceCsrfRefresh();
  });
}
